
// src/hooks/useCategories.js

import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchCategoriesPaginated } from "../../api/workoutApi";

/**
 * React Query hook that fetches categories with infinite scroll/pagination.
 *
 * Returns the standard useInfiniteQuery result object:
 *   { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage, ... }
 *
 * `data` will contain pages with categories arrays and pagination info.
 */
export const useCategoriesInfinite = (limit = 10) =>
  useInfiniteQuery({
    queryKey: ["categories", "infinite", limit],
    queryFn: ({ pageParam = 1 }) => 
      fetchCategoriesPaginated({ page: pageParam, limit }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

// Keep original hook for backward compatibility
export const useCategories = () => {
  const { data, ...rest } = useInfiniteQuery({
    queryKey: ["categories", "infinite", 50],
    queryFn: ({ pageParam = 1 }) => 
      fetchCategoriesPaginated({ page: pageParam, limit: 50 }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all categories from all pages for backward compatibility
  const allCategories = data?.pages.flatMap(page => page.categories) || [];
  
  return {
    data: allCategories,
    isLoading: rest.isLoading,
    isError: rest.isError,
    error: rest.error,
    refetch: rest.refetch,
    ...rest,
  };
};